import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { useCallback, useEffect, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useGeneratePurchaseOrderPdfMutation, usePurchaseOrderByIdQuery } from 'api';

interface UsePurchaseOrderActionsProps {
  purchaseOrderId: string;
}

export const usePurchaseOrderActions = ({ purchaseOrderId }: UsePurchaseOrderActionsProps) => {
  const { handleSuccess, handleError } = useNotificationMessages();
  const navigate = useNavigate();
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [isSendingOrder, setIsSendingOrder] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [supplierOfferModalState, setSupplierOfferModalState] = useState<boolean>(false);

  const { data = {} } = usePurchaseOrderByIdQuery({ id: purchaseOrderId });
  const { purchaseOrder = {} } = data;

  const [generatePdf] = useGeneratePurchaseOrderPdfMutation();

  useEffect(() => {
    if (purchaseOrder?.pdfGenerated) {
      setPdfGenerated(true);
    }
  }, [purchaseOrder?.pdfGenerated]);

  const onSupplierOfferModalStateChange = useCallback(() => setSupplierOfferModalState((prevState) => !prevState), []);

  const handleGeneratePdf = useCallback(async () => {
    try {
      setIsGeneratingPdf(true);

      // Call the real PDF generation API
      await generatePdf({ purchaseOrderId });

      setPdfGenerated(true);
      handleSuccess('PDF has been generated successfully.');
    } catch (error) {
      console.error('PDF generation failed:', error);
      handleError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [handleSuccess, handleError, purchaseOrderId, generatePdf]);

  const handleSendOrder = useCallback(async () => {
    if (!pdfGenerated) {
      handleError('Please generate PDF before sending the order.');
      return;
    }

    try {
      setIsSendingOrder(true);

      // Navigate to Send Purchase Order page
      navigate(`${ADMIN_ROUTES.purchaseOrder.view}/${purchaseOrderId}/send-order`);
    } catch (error) {
      console.log(error, '✅ => er');
      handleError('Failed to open send order page. Please try again.');
    } finally {
      setIsSendingOrder(false);
    }
  }, [pdfGenerated, navigate, purchaseOrderId, handleError]);

  return {
    purchaseOrder,
    pdfGenerated,
    isGeneratingPdf,
    isSendingOrder,
    handleGeneratePdf,
    handleSendOrder,
    supplierOfferModalState,
    onSupplierOfferModalStateChange,
  };
};
